import { useState, useEffect } from 'react';
import { Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material'
import { useNavigate } from "react-router-dom";
import * as GolferHelper from '../library/GolferHelper';
import Golfer, { LoftShots } from '../classes/Golfer';
import * as ShotSelection from '../classes/ShotSelection';
import { Person } from '@mui/icons-material';
import GolferMenu from './GolferMenu';
import { HtmlTooltip } from './HtmlTooltip';
import * as TutorialHelper from '../library/TutorialHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';

export default function SelectShots() {
    const navigate = useNavigate();
    const intl = useIntl();
    const [golfer, setGolfer] = useState(new Golfer());
    const [showErrors, setShowErrors] = useState(false);
    const [pageIsValid, setPageIsValid] = useState(true);
    const [sortedLofts, setSortedLofts] = useState([] as LoftShots[]);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const intlSwing = intl.formatMessage({ id: "shot.selection.swing" });
    //convert the shot type enum to international text
    const intlShotName = (name: string) => {
        return intl.formatMessage({ id: "shot.selection." + name.replace(" ", "").toLowerCase() });
    }

    useEffect((): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_shot_selection'
            }
        });

        const asyncProcess = async () => {
            // resetTimeout();
            setShowTutorial(await TutorialHelper.isActive());
            let newGolfer = await GolferHelper.getCurrentGolfer()
            //sort the loft shots just for this screen
            let newLofts = sortLofts(newGolfer.selectedLofts);
            setSortedLofts(newLofts);
            setGolfer(newGolfer);
        };
        asyncProcess();
    }, []);


    //re-order the loft shot selection array based on the sort order Map collection. This is a special sort order just for this screen.
    //need to recreate the collection from scratch so it doesn't accidentally change the original collection via references.
    //also can't use the spread operator on arrays of arrays b/c it only does a shallow spread.
    const sortLofts = (lofts: LoftShots[]): LoftShots[] => {
        let newLofts = [] as LoftShots[];  //empty collection to hold the new lofts
        lofts.forEach(loft => {
            let newShots = [...loft.shots].sort((a, b) => {
                let aOrder = ShotSelection.ShotTypeSortOrder.get(a.shotType) as number;
                let bOrder = ShotSelection.ShotTypeSortOrder.get(b.shotType) as number;
                return aOrder - bOrder;
            });
            newLofts.push(new LoftShots(loft.lineNumber, loft.loft, newShots));
        })
        return newLofts;
    }

    const handleShotButton = (loft: string, shotName: string): void => {
        let newGolfer = { ...golfer };
        let currentLoft = newGolfer.selectedLofts.find(l => l.loft === loft);
        let newShot = currentLoft!.shots.find(s => s.shotType === shotName) as ShotSelection.ShotSelection;
        newShot.selected = !newShot.selected;
        validatePage(newGolfer);
        setGolfer(newGolfer);
    }

    const validatePage = (golfer: Golfer): boolean => {
        let isValid = true;
        //if any loft doesn't have at least one shot selected, page is invalid
        golfer.selectedLofts.forEach(sl => {
            isValid = sl.shots.filter(sh => sh.selected).length === 0 ? false : isValid;
        })
        setPageIsValid(isValid);
        return isValid;
    }

    const handleSubmit = async () => {
        if (validatePage(golfer)) {
            //this is just for logging
            var selectedLofts = golfer.selectedLofts.map(loft => {
                return {
                    loft: loft.loft,
                    shots: loft.shots.filter(shot => shot.selected).map(shot => shot.shotType).join(", ")
                }
            })
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_shot_selection_rank_shots',
                    q_use: selectedLofts
                }
            });

            //use the selected shots to create a list of shots for fitting  - delete if app works without this code
            // let shots = GolferHelper.getShotsForFitting(golfer.selectedLofts);
            // golfer.shotsForFitting = shots;
            //rank the shots by only using the ones selected
            sortedLofts.forEach(loft => {
                var priority = 0;
                loft.shots.forEach(shot => {
                    if (shot.selected) {
                        shot.priority = priority++;
                    }
                })
            })

            golfer.selectedLofts = sortedLofts;
            await GolferHelper.upsertGolfer(golfer);
            navigate('/apps/wedgefitting/rankshots');
        } else {
            setShowErrors(true);
        }
    }

    const handleBack = () => {
        navigate('/apps/wedgefitting/inbag');
    }

    const cbGolferUpdate = async (cbGolfer: Golfer) => {
        let newGolfer = { ...golfer, firstName: cbGolfer.firstName, lastName: cbGolfer.lastName, fullName: cbGolfer.fullName, email: cbGolfer.email, phone: cbGolfer.phone, smsOptIn: cbGolfer.smsOptIn  };
        setGolfer(newGolfer);
        await GolferHelper.upsertGolfer(newGolfer);
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId === id;
    }

    const handleTutorialNext = () => {
        if (tutorialId === 1) {
            navigate('/apps/wedgefitting/rankshots');
        } else {
            setTutorialId(tutorialId + 1);
        }
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    return (
        <div className="shot-selection-form">
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ marginTop: '10px', display: { xs: 'none', md: 'flex' } }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '-10px', height: '2em' }}></Person>
                                        <GolferMenu golfer={golfer} cbgolferupdate={cbGolferUpdate}></GolferMenu>
                                    </Typography>

                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', justifyContent: 'center', marginTop: '7px' }}>
                                        <FormattedMessage id="shot.selection.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleBack} className='cancel-button'><FormattedMessage id="general.back" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button' sx={{ width: '175px !important' }}><FormattedMessage id="shot.selection.rank" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth='lg' sx={{ marginTop: '80px' }}>
                <HtmlTooltip
                    title={
                        <>
                            <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.use.wedges" /></Typography>
                            <div style={{ textAlign: 'right' }}>
                                <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                            </div>
                        </>
                    }
                    open={showPopup(0)}
                    placement={'top'}
                    arrow={false}
                    maxwidth={600}
                >

                    <Grid container className='row-error'>
                        <Grid item xs={12} className='label' sx={{ color: 'red', minHeight: '24px', marginBottom: '10px' }}><span>{`${!pageIsValid && showErrors ? intlSwing : ""}`}</span>
                        </Grid>
                    </Grid>
                </HtmlTooltip>
                <Grid container className='form-label'>
                    <Grid item xs={12}><span><FormattedMessage id="shot.selection.shots" /></span></Grid>
                </Grid>
                <Grid container className='row shot-grid' sx={{ marginTop: '20px' }}>
                    {sortedLofts.map((loft) => {
                        return (
                            <Grid item key={loft.loft} xs={3} md={2}>
                                <Grid container className='row' sx={{ marginTop: '20px' }}>
                                    <Grid item xs={12} sx={{ marginLeft: '-5px' }}>
                                        <span className='form-label'>{loft.loft}&#176;</span>
                                    </Grid>
                                </Grid>
                                {loft.shots.map((shot, ix) => {
                                    return (
                                        <Grid container className='rank-shots-buttons' key={ix} sx={{ marginTop: '20px', paddingLeft: '10px' }}>
                                            <Grid item xs={12} sx={{ height: '100%' }}>
                                                <Grid container sx={{ width: '100%', paddingTop: '5px' }}>
                                                    <Grid item sm={12} className='form-row-buttons'>
                                                        <Button disableRipple type="submit" name='shotSelection' value={shot.shotType} variant="outlined" className={`${shot.selected ? "selected" : ""}`} style={{ width: '100%', height: '75px', marginTop: '0px', justifyContent: 'left' }} onClick={() => handleShotButton(loft.loft, shot.shotType)}>{intlShotName(shot.shotType)}</Button>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                    )
                                })
                                }
                            </Grid>
                        )
                    })
                    }
                    <HtmlTooltip
                        title={
                            <>
                                <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.select.type" /></Typography>
                                <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.preselect.responses" /></Typography>
                                <div style={{ textAlign: 'right' }}>
                                    <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                    <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                </div>
                            </>
                        }
                        open={showPopup(1)}
                        placement={'left'}
                        arrow={false}
                        maxwidth={400}
                    >
                        <Grid item xs={1}></Grid>
                    </HtmlTooltip>
                </Grid>

            </Container>
        </div>
    )
}